import React, { Fragment, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import type { ComparisonRow, SummaryResponse, ComparisonQuery, ScrapperLink } from '../services/comparisonService';
import { dateFromDaysBack } from '../services/comparisonService';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchableSelect from '../components/SearchableSelect';
import { comparisonCache } from '../services/comparisonCache';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, Filler);

const LIMIT = 50;

// ---- Pretty label helpers (normTitle output → display) -----------------------

const PRETTY_LABELS: Record<string, string> = {
  amazonin: 'Amazon',
  flipkartcom: 'Flipkart',
  vedantcomputerscom: 'Vedant Computers',
  primeabgbcom: 'Prime ABGB',
  pcstudioin: 'PC Studio',
  elitehubscom: 'EliteHubs',
  vishalperipheralscom: 'Vishal Peripherals',
  mdcomputersin: 'MD Computers',
  nationalpcin: 'National PC',
  onlyssdcom: 'OnlySSD',
  smcinternationalin: 'SMC International',
  tpstechin: 'TPS Tech',
};

const normTitle = (title: string): string =>
  title.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The backend's canonical form of a competitor title (trimmed, lower-cased,
 * without a leading "www."). The applied company must be canonicalised the
 * same way before it is compared with the competitor list, otherwise the
 * column order the UI renders and the order the CSV writes can drift apart.
 */
const canonTitle = (title: string): string =>
  title.trim().toLowerCase().replace(/^www\./, '');

/**
 * Data rows in a CSV file — the first line is the header. Used as a fallback
 * when the server does not send its row-count headers.
 */
const countCsvRows = async (blob: Blob): Promise<number> => {
  const text = await blob.text();
  const lines = text.split('\r\n').filter((l) => l.trim() !== '');
  return Math.max(0, lines.length - 1);
};

const prettyLabel = (title: string): string =>
  PRETTY_LABELS[normTitle(title)] ?? title;

// Deterministic sparkline color from title
const hashColor = (title: string): string => {
  const hue = Array.from(title).reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);
  return `hsl(${hue}, 65%, 45%)`;
};

interface QueryParams {
  filters: { category: string; brand: string; search: string };
  inStockOnly: boolean;
  priceFilter: string;
  company: string;
  daysBack: number;
  refresh?: boolean;
}

const formatINR = (v: number | string | undefined | null) => {
  if (v === null || v === undefined || v === 'no product') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '—';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatDate = (v: string | undefined | null) => {
  if (!v) return '—';
  return v.slice(0, 10);
};

const formatDateTime = (v: string | undefined | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  const dd = String(d.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mon = months[d.getMonth()];
  const yr = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd} ${mon} ${yr}, ${hh}:${mm}`;
};

const Comparison: React.FC = () => {
  const api = useApi();
  const [filters, setFilters] = useState({ category: '', brand: '', search: '' });
  const [daysBack, setDaysBack] = useState(0);
  const [company, setCompany] = useState('all');
  // The company filter is applied by the Search button, so the COLUMN order
  // follows the applied company — not the pending one in the dropdown.
  const [appliedCompany, setAppliedCompany] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [competitors, setCompetitors] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addProdId] = useState(0);
  const [addTitle, setAddTitle] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addStatus, setAddStatus] = useState('w');
  const [addTitles] = useState<string[]>([]);
  const [addLoading] = useState(false);
  // Which export is in flight — 'all' for the whole view, a product id for the
  // single-row export. Drives the loader and disables the Export buttons.
  const [downloading, setDownloading] = useState<number | 'all' | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [categoryRequired, setCategoryRequired] = useState(false);
  // True once a filter has been applied (category + Search). The export needs
  // one: without it the file would carry the whole catalog, which is never
  // what the user asked for.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [analyticsRow, setAnalyticsRow] = useState<number | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [sparklineData, setSparklineData] = useState<Record<string, unknown>[]>([]);
  const [sparklineLoading, setSparklineLoading] = useState(false);
  const [sparklineError, setSparklineError] = useState(false);
  const [sparklineDaysBack, setSparklineDaysBack] = useState(7);
  const [scrapperLinks, setScrapperLinks] = useState<Record<number, ScrapperLink[]>>({});
  const sparklineCache = useRef<Record<string, Record<string, unknown>[]>>({});
  const sparklineController = useRef<AbortController | null>(null);
  const seqRef = useRef(0);
  const skipRef = useRef(0);
  const appendFailRef = useRef(0);
  const rowsRef = useRef<ComparisonRow[]>([]);
  rowsRef.current = rows;
  const refreshRef = useRef(false);
  const queryRef = useRef<QueryParams>({ filters, daysBack, company, priceFilter, inStockOnly });
  const drawerInnerRef = useRef<HTMLDivElement>(null);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  }, []);

  const loadPage = useCallback(async (skipValue: number, append: boolean) => {
    const seq = seqRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    const q: ComparisonQuery = {
      filters: queryRef.current.filters,
      inStockOnly: queryRef.current.inStockOnly,
      priceFilter: queryRef.current.priceFilter,
      company: queryRef.current.company,
      daysBack: queryRef.current.daysBack,
      skip: skipValue,
      limit: LIMIT,
      refresh: queryRef.current.refresh,
    };
    try {
      const data = await api.comparison.list(q);
      if (seqRef.current !== seq) return;
      if (append) {
        const seen = new Set(rowsRef.current.map((r) => r.id));
        const fresh = (data || []).filter((r) => !seen.has(r.id));
        skipRef.current += LIMIT;
        if ((data || []).length < LIMIT || fresh.length === 0) setHasMore(false);
        setRows((prev) => [...prev, ...fresh]);
      } else {
        setRows(data || []);
        setHasMore((data || []).length === LIMIT);
        skipRef.current = LIMIT;
        const sum = await api.comparison.summary(q);
        if (seqRef.current !== seq) return;
        setSummary(sum);
      }
    } catch (err) {
      if (seqRef.current !== seq) return;
      if (append) {
        appendFailRef.current = Date.now();
        showToast('Failed to load more');
        setLoadingMore(false);
      } else {
        setError((err as Error).message);
      }
    } finally {
      if (seqRef.current !== seq) return;
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [api, showToast]);

  useEffect(() => {
    api.comparison.categories()
      .then((r) => setCategories(r.categories || []))
      .catch(() => setCategories([]));
  }, [api]);

  useEffect(() => {
    api.comparison.brands()
      .then((r) => setBrands(r.brands || []))
      .catch(() => setBrands([]));
  }, [api]);

  // Fetch competitors on mount
  useEffect(() => {
    api.comparison.competitors()
      .then((r) => setCompetitors(r.competitors || []))
      .catch(() => setCompetitors([]));
  }, [api]);

  // Outside-click handler for admin menu
  useEffect(() => {
    if (!showAdminMenu) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-admin-menu-trigger], [data-admin-menu-panel]')) setShowAdminMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAdminMenu]);

  // The collapsed drawer keeps its content mounted (required for the height
  // transition), so mark it inert while hidden to keep it out of the tab order.
  useEffect(() => {
    const el = drawerInnerRef.current;
    if (!el) return;
    if (showAdvanced) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [showAdvanced]);

  const reset = useCallback(() => {
    if (!filters.category) {
      setCategoryRequired(true);
      return;
    }

    // Capture current refresh flag before resetting it
    const doRefresh = refreshRef.current;
    refreshRef.current = false; // reset immediately after capture

    setCategoryRequired(false);
    setHasAppliedFilter(true);
    setAppliedCompany(company);
    queryRef.current = { filters, daysBack, company, priceFilter, inStockOnly, refresh: doRefresh };
    seqRef.current++;
    skipRef.current = 0;
    setRows([]);
    setHasMore(false);
    setLoadingMore(false);
    loadPage(0, false);
  }, [loadPage, filters, daysBack, company, priceFilter, inStockOnly]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || loading || loadingMore) return;
      if (Date.now() - appendFailRef.current < 3000) return;
      const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 400;
      if (scrolledToBottom) loadPage(skipRef.current, true);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, loadingMore, loadPage]);

  const fetchSparkline = useCallback(async (productId: number, daysBack: number) => {
    const cacheKey = `${productId}_${daysBack}`;
    if (sparklineCache.current[cacheKey]) {
      setSparklineData(sparklineCache.current[cacheKey]);
      setSparklineError(false);
      setSparklineLoading(false);
      return;
    }
    sparklineController.current?.abort();
    sparklineController.current = new AbortController();
    setSparklineData([]);
    setSparklineLoading(true);
    setSparklineError(false);
    try {
      const data = await api.comparison.single(productId, daysBack, sparklineController.current.signal);
      sparklineCache.current[cacheKey] = data;
      setSparklineData(data);
      setSparklineError(false);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setSparklineData([]);
      setSparklineError(true);
    } finally {
      setSparklineLoading(false);
    }
  }, [api]);

  const openAnalytics = useCallback(async (productId: number) => {
    setAnalyticsRow(productId);
    setSparklineDaysBack(7);
    await fetchSparkline(productId, 7);
    try {
      const linksRes = await api.comparison.scrapperLinks(productId);
      setScrapperLinks((prev) => ({ ...prev, [productId]: linksRes.links || [] }));
    } catch {
      // non-fatal: keep existing links state
    }
  }, [api, fetchSparkline]);

  // handleSaveLink sends the raw title directly (company filter uses exact wlist.title)
  const handleSaveLink = async (key: string) => {
    if (!analyticsRow) return;
    const cleanUrl = editUrl.trim();
    if (!cleanUrl || !/^https?:\/\//.test(cleanUrl)) return;
    setSavingEdit(true);
    try {
      await api.comparison.upsertLink({ prod_id: analyticsRow, title: key, url: cleanUrl });
      const linksRes = await api.comparison.scrapperLinks(analyticsRow);
      setScrapperLinks((prev) => ({ ...prev, [analyticsRow]: linksRes.links || [] }));
      setEditingKey(null);
      setEditUrl('');
      showToast('Link saved');
    } catch (err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      const msg = resp || (err as Error).message || 'Failed to save';
      showToast(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveNew = async () => {
    if (!addTitle || !addUrl) { showToast('Title and URL required'); return; }
    try {
      await api.scrappers.create({ prod_id: addProdId, title: addTitle, url: addUrl, status: addStatus });
      setShowAddModal(false);
      showToast('Scraper added');
      reset();
    } catch { showToast('Failed to add scraper'); }
  };

  const handleDownload = async (productId: number | 'all' | null) => {
    if (downloading !== null) return; // one export at a time
    // The export mirrors the table, so it needs an applied filter — without
    // one it would dump the whole catalog, which is never what the user meant.
    if (!hasAppliedFilter) {
      showToast('Select a category and press Search before exporting');
      return;
    }
    setDownloading(productId);
    try {
      // The export uses the APPLIED query (queryRef), so it always matches the
      // filters the table is showing — and it carries the WHOLE filtered set,
      // not just the rows scrolled into the viewport.
      const result = await api.comparison.exportCsv(
        queryRef.current,
        productId === 'all' || productId === null ? undefined : productId,
      );

      // A file with no data rows is not a successful export — say so instead
      // of handing the user a header-only file.
      const exportedRows = result.exportedRows ?? (await countCsvRows(result.blob));
      if (exportedRows === 0) {
        showToast('No rows match the current filters');
        return;
      }

      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      // The server names the file when it sends Content-Disposition; the
      // client-side name is the fallback for a backend that does not.
      a.download = result.filename || `comparison-${dateFromDaysBack(queryRef.current.daysBack)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking in the same tick can cancel the download in some browsers —
      // give the browser a moment to pick the blob up first.
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const total = result.totalRows;
      if (total !== null && total > exportedRows) {
        showToast(`CSV downloaded — ${exportedRows} of ${total} rows (limit reached, narrow the filter)`);
      } else {
        showToast(`CSV downloaded — ${exportedRows} row${exportedRows === 1 ? '' : 's'}`);
      }
    } catch (err) {
      showToast((err as Error).message || 'Export failed');
    } finally {
      setDownloading(null);
    }
  };

  // Computed summary metrics
  // Brand counts live at the top level in the pruned backend response, but the
  // deployed backend still nests them in `horizotalChartBar`. Frontend and
  // backend deploy independently, so read whichever shape actually arrived -
  // otherwise "Tracked Items" silently renders 0.
  const brandCounts = summary?.brands && Object.keys(summary.brands).length > 0
    ? summary.brands
    : summary?.horizotalChartBar?.brands ?? {};
  const totalTracked = Object.values(brandCounts).reduce((a, b) => a + (Number(b) || 0), 0);
  const [cheaper, highest] = summary?.pieChart.pieVal || [0, 0, 0];
  const mdAdvantagePct = totalTracked > 0 ? Math.round(cheaper / totalTracked * 100) : 0;

  // Competitor column order: with a company filter applied that company sits
  // FIRST — right after the MD column — and the remaining companies follow.
  // The order is derived from the APPLIED company, so picking a company in the
  // drawer without pressing Search changes nothing yet. The applied company is
  // canonicalised exactly like the backend does it, so the table and the CSV
  // always order the columns the same way.
  const competitorTitles = useMemo(() => {
    const applied = canonTitle(appliedCompany);
    if (applied === 'all') return competitors;
    if (!competitors.includes(applied)) return competitors;
    return [applied, ...competitors.filter((t) => t !== applied)];
  }, [competitors, appliedCompany]);

  // Build dynamic COLS: Product | MD | each competitor (with prettyLabel)
  const competitorCols = competitorTitles.map((title) => ({
    key: title,
    label: prettyLabel(title),
    width: 'w-28',
  }));
  const COLS = [
    { key: 'product', label: 'Product', width: 'min-w-[200px]' },
    { key: 'md', label: 'MD Computers', width: 'w-32' },
    ...competitorCols,
  ];

  const advancedActiveCount =
    (filters.brand !== '' ? 1 : 0) +
    (inStockOnly ? 1 : 0);

  return (
    <div className="space-y-0">
      <style>{`
        .drawer-grid { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 300ms ease-in-out; }
        .drawer-grid.open { grid-template-rows: 1fr; }
        .drawer-inner { overflow: hidden; }
      `}</style>
      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-4 right-4 z-50 bg-sidebar-bg text-white px-4 py-3 rounded-lg shadow-xl border border-primary-fixed-dim/30 flex items-center space-x-3 animate-toast">
          <CheckIcon className="text-success text-[20px]" />
          <span className="text-xs font-medium">{toastMsg}</span>
        </div>
      )}

      {/* Export progress — the backend builds the CSV in memory and returns it
          in one response, so the spinner stays up until the browser holds the
          whole file. */}
      {downloading !== null && (
        <div className="fixed top-4 right-4 z-50 bg-sidebar-bg text-white px-4 py-3 rounded-lg shadow-xl border border-primary-fixed-dim/30 flex items-center space-x-3">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
          <span className="text-xs font-medium">Preparing CSV export…</span>
        </div>
      )}

      {/* Filter bar */}
      <div className="px-4 lg:px-6 py-3 bg-surface border-b border-border-subtle">
        <div className="relative flex flex-wrap items-end gap-3 pr-[56px]">
          {/* Product Search */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium">Search</label>
            <div className="flex items-center relative min-w-[400px] flex-1 max-w-xs">
              <SearchIcon className="absolute left-3 text-on-surface-variant/50 text-[16px] pointer-events-none" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && reset()}
                placeholder="Title, SKU, or ID..."
                className="bg-surface-lowest border border-border-subtle focus-within:ring-2 focus-within:ring-primary-container focus:border-transparent rounded px-3 py-1.5 pl-9 text-body-sm w-full outline-none transition-all text-on-surface placeholder:text-on-surface-variant/50 h-8"
              />
            </div>
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium">
              Category{categoryRequired && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <SearchableSelect
              options={categories}
              value={filters.category}
              onChange={(v) => { setFilters({ ...filters, category: v }); setCategoryRequired(false); }}
              placeholder="Search category..."
              emptyLabel="All categories"
              className="min-w-[170px] flex-shrink-0"
            />
          </div>

          {/* Timeframe */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium">Timeframe</label>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value, 10))}
              className="bg-surface-lowest border border-border-subtle rounded px-3 py-1.5 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer h-8 min-w-[100px]"
            >
              {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                <option key={d} value={d}>{d === 0 ? 'Today' : `${d}d ago`}</option>
              ))}
            </select>
          </div>

          {/* Search button */}
          <button
            onClick={reset}
            className="h-8 px-3 bg-primary-container text-on-primary rounded text-[14px] hover:bg-primary active:scale-95 flex items-center gap-1.5"
          >
            <SearchIcon className="text-[16px]" />
            Search
          </button>

          {/* Advanced Filters toggle */}
          <button
            onClick={() => setShowAdvanced(v => !v)}
            aria-expanded={showAdvanced}
            aria-controls="advanced-filters-drawer"
            className="h-8 px-2 rounded border bg-surface-lowest border-border-subtle hover:border-primary/30 cursor-pointer flex items-center gap-2 text-body-sm text-on-surface"
          >
            <span>Advanced Filters</span>
            {advancedActiveCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-on-primary text-[10px] font-bold">
                {advancedActiveCount}
              </span>
            )}
            <ChevronDownIcon className={`text-[16px] transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {/* Admin Actions dropdown — absolute positioned */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <button
              data-admin-menu-trigger
              onClick={() => setShowAdminMenu(v => !v)}
              className="h-8 w-9 rounded border bg-surface-lowest border-border-subtle hover:border-primary/30 cursor-pointer flex items-center justify-center"
            >
              <SettingsIcon className="text-[16px] text-on-surface" />
            </button>
            {showAdminMenu && (
              <div
                data-admin-menu-panel
                className="absolute top-full right-0 mt-1 z-50 bg-surface-lowest border border-border-subtle rounded-lg shadow-lg py-1 min-w-[160px]"
              >
                {daysBack === 0 && (
                  <button
                    onClick={() => { refreshRef.current = true; reset(); setShowAdminMenu(false); }}
                    className="flex items-center gap-2 px-3 py-2 text-body-sm hover:bg-primary/10 cursor-pointer w-full text-left text-on-surface"
                  >
                    <RefreshIcon className="text-[16px]" />
                    Refresh
                  </button>
                )}
                <button
                  onClick={() => { comparisonCache.clearCache(); setShowAdminMenu(false); }}
                  className="flex items-center gap-2 px-3 py-2 text-body-sm hover:bg-primary/10 cursor-pointer w-full text-left text-on-surface"
                >
                  <DeleteSweepIcon className="text-[16px]" />
                  Clear Cache
                </button>
                <button
                  onClick={() => { setShowAdminMenu(false); void handleDownload('all'); }}
                  disabled={downloading !== null || !hasAppliedFilter}
                  title={hasAppliedFilter
                    ? 'Download the current comparison view as CSV'
                    : 'Select a category and press Search first'}
                  className="flex items-center gap-2 px-3 py-2 text-body-sm hover:bg-primary/10 cursor-pointer w-full text-left text-on-surface disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DownloadIcon className="text-[16px]" />
                  Export
                </button>
                {!hasAppliedFilter && (
                  <p className="px-3 pb-1 pt-1 text-[11px] leading-snug text-on-surface-variant">
                    Select a category and press Search to enable export.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* validation message — outside the bar, below it */}
        {categoryRequired && (
          <div className="px-4 text-[13px] text-red-600 mt-1">
            Please select a category
          </div>
        )}

        {/* Active filter chips */}
        {(filters.brand || company !== 'all' || priceFilter !== 'all' || inStockOnly) ? (
          <div className="flex flex-wrap gap-2 items-center px-4 py-2">
            <span className="text-[11px] text-on-surface/60 uppercase tracking-wide">Active:</span>
            {filters.brand && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[12px] font-medium">
                {filters.brand}
                <button
                  onClick={() => setFilters(f => ({ ...f, brand: '' }))}
                  className="text-on-surface/40 hover:text-on-surface ml-0.5 text-[14px] leading-none"
                  aria-label={`Remove brand filter: ${filters.brand}`}
                >×</button>
              </span>
            )}
            {company !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[12px] font-medium">
                {prettyLabel(company)}
                <button
                  onClick={() => setCompany('all')}
                  className="text-on-surface/40 hover:text-on-surface ml-0.5 text-[14px] leading-none"
                  aria-label={`Remove company filter`}
                >×</button>
              </span>
            )}
            {priceFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[12px] font-medium">
                {priceFilter === 'cheaper' ? 'MD Cheaper' : priceFilter === 'higher' ? 'MD Higher' : priceFilter}
                <button
                  onClick={() => setPriceFilter('all')}
                  className="text-on-surface/40 hover:text-on-surface ml-0.5 text-[14px] leading-none"
                  aria-label="Remove price position filter"
                >×</button>
              </span>
            )}
            {inStockOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[12px] font-medium">
                In Stock
                <button
                  onClick={() => setInStockOnly(false)}
                  className="text-on-surface/40 hover:text-on-surface ml-0.5 text-[14px] leading-none"
                  aria-label="Remove stock filter"
                >×</button>
              </span>
            )}
          </div>
        ) : (
          <div className="h-6" />
        )}

        {/* Advanced Filters drawer */}
        <div
          id="advanced-filters-drawer"
          className={`drawer-grid ${showAdvanced ? 'open' : ''}`}
        >
          <div className="drawer-inner" ref={drawerInnerRef}>
            <div className="pt-3 mt-3 border-t border-border-subtle">
              <div className="flex flex-wrap items-end gap-3">
                {/* Brand */}
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium block mb-1">BRAND</label>
                  <SearchableSelect
                    options={brands}
                    value={filters.brand}
                    onChange={(v) => setFilters({ ...filters, brand: v })}
                    placeholder="All brands"
                    emptyLabel="All brands"
                    className="min-w-[160px]"
                    disabled={!showAdvanced}
                  />
                </div>
                {/* Company */}
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium block mb-1">COMPANY</label>
                  <select
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="bg-surface-lowest border border-border-subtle rounded px-3 py-1.5 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer min-w-[180px]"
                  >
                    <option value="all">All Companies</option>
                    {competitors.map((title) => (
                      <option key={title} value={title}>{prettyLabel(title)}</option>
                    ))}
                  </select>
                </div>
                {/* Position */}
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium block mb-1">POSITION</label>
                  <select
                    value={priceFilter}
                    onChange={(e) => setPriceFilter(e.target.value)}
                    className="bg-surface-lowest border border-border-subtle rounded px-3 py-1.5 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer min-w-[140px]"
                  >
                    <option value="all">Any Position</option>
                    <option value="cheaper">MD Cheaper</option>
                    <option value="higher">MD Higher</option>
                  </select>
                </div>
                {/* Status */}
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-on-surface/60 font-medium block mb-1">STATUS</label>
                  <div className="flex items-center gap-2 h-8">
                    <input
                      type="checkbox"
                      id="stock-filter"
                      checked={inStockOnly}
                      onChange={(e) => setInStockOnly(e.target.checked)}
                      className="h-4 w-4 rounded border-border-subtle text-primary focus:ring-primary/20 cursor-pointer accent-primary"
                    />
                    <label
                      htmlFor="stock-filter"
                      className="text-[11px] text-on-surface/60 font-medium cursor-pointer"
                    >
                      In Stock
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary metrics strip */}
      {summary && (
        <div className="px-4 lg:px-6 py-3 bg-surface border-b border-border-subtle">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="flex items-center gap-3 px-4 py-2 bg-surface-lowest rounded-lg border-l-4 border-primary">
              <Inventory2Icon className="text-primary text-[20px]" />
              <div>
                <div className="font-headline-sm font-bold text-on-surface">{totalTracked.toLocaleString()}</div>
                <div className="text-label-caps text-on-surface-variant text-[10px]">Tracked Items</div>
              </div>
            </div>
            {priceFilter === 'all' && (
              <div className="flex items-center gap-3 px-4 py-2 bg-surface-lowest rounded-lg border-l-4 border-success">
                <TrendingDownIcon className="text-success text-[20px]" />
                <div>
                  <div className="font-headline-sm font-bold text-success">{mdAdvantagePct}%</div>
                  <div className="text-label-caps text-on-surface-variant text-[10px]">MD Price Advantage</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 px-4 py-2 bg-surface-lowest rounded-lg border-l-4 border-danger">
              <TrendingUpIcon className="text-danger text-[20px]" />
              <div>
                <div className="font-headline-sm font-bold text-danger">{highest}</div>
                <div className="text-label-caps text-on-surface-variant text-[10px]">Above Market</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 bg-surface-lowest rounded-lg border-l-4 border-warning">
              <TrendingFlatIcon className="text-warning text-[20px]" />
              <div>
                <div className="font-headline-sm font-bold text-on-surface">{summary?.mdVsMarket ? summary.mdVsMarket.marketAvg.toFixed(0) : '—'}</div>
                <div className="text-label-caps text-on-surface-variant text-[10px]">Avg Market Price</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick stats below table header */}
      {rows.length > 0 && !loading && (
        <div className="px-4 lg:px-6 py-2 bg-surface border-b border-border-subtle flex items-center gap-4 text-body-sm text-on-surface-variant">
          <span>{rows.length} products shown</span>
          <span className="text-border-subtle">|</span>
          <span>{rows.filter((r) => Number(r.stock) > 0).length} in stock</span>
          <span className="text-border-subtle">|</span>
          <span>{rows.filter((r) => {
            const originalPrice = Number(r.price) || 0;
            const offerPrice = r.offer_price != null ? Number(r.offer_price) : null;
            const currentPrice = offerPrice ?? originalPrice;
            const comps = competitorTitles
              .map((t) => parseFloat(String((r.competitors as Record<string, { price: string }>)?.[t]?.price ?? 'NaN')))
              .filter((p) => !isNaN(p));
            return currentPrice > 0 && comps.length > 0 && currentPrice > Math.min(...comps);
          }).length} overpriced</span>
        </div>
      )}

      {/* Main table */}
      <div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="bg-surface border border-border-subtle rounded-xl p-6 text-center">
            <ErrorIcon className="text-[36px] text-danger mb-2 mx-auto" />
            <p className="font-headline-sm text-danger">{error}</p>
            <button onClick={reset} className="mt-3 text-primary font-body-sm hover:underline">Try again</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-surface border border-border-subtle rounded-xl p-8 text-center">
            <SearchIcon className="text-[36px] text-outline mb-2 mx-auto" />
            <p className="font-headline-sm text-on-surface">No products found</p>
            <p className="font-body-sm text-on-surface-variant mt-1">Adjust filters or search term.</p>
          </div>
        ) : (
          <>
            {/* Mobile: card grid — hidden on large screens */}
            <div className="lg:hidden px-4 py-4 space-y-3">
              {rows.map((r) => {
                const originalPrice = Number(r.price) || 0;
                const offerPrice = r.offer_price != null ? Number(r.offer_price) : null;
                const currentPrice = offerPrice ?? originalPrice;
                const mdStock = Number(r.stock) || 0;
                const rowCompetitors = (r.competitors as Record<string, { price: string; stock: string; link: string }>) || {};

                const compData = competitorTitles.reduce<Record<string, { price: number | null; stock: string | null; delta: number | null; isLowest: boolean }>>((acc, title) => {
                  const comp = rowCompetitors[title];
                  const rawPrice = comp?.price;
                  const cp = rawPrice !== 'no product' && rawPrice != null ? parseFloat(String(rawPrice)) : null;
                  const cStock = comp?.stock ?? null;
                  const delta = cp !== null && currentPrice > 0 ? ((currentPrice - cp) / cp) * 100 : null;
                  acc[title] = { price: cp, stock: cStock, delta, isLowest: false };
                  return acc;
                }, {} as Record<string, { price: number | null; stock: string | null; delta: number | null; isLowest: boolean }>);

                const pricedComps = Object.values(compData).filter((c) => c.price !== null && Number(c.price) > 0 && c.stock !== 'Out Of Stock');
                const lowestComp = pricedComps.length > 0 ? Math.min(...pricedComps.map((c) => c.price!)) : null;

                if (lowestComp !== null) {
                  for (const title of competitorTitles) {
                    if (compData[title].price === lowestComp) { compData[title].isLowest = true; break; }
                  }
                }

                const allPrices = pricedComps.map((c) => c.price!);
                if (currentPrice > 0) allPrices.push(currentPrice);
                const rowMin = allPrices.length > 0 ? Math.min(...allPrices) : null;
                const rowMax = allPrices.length > 0 ? Math.max(...allPrices) : null;
                const mdIsMin = rowMin !== null && currentPrice === rowMin;
                const mdIsMax = rowMax !== null && currentPrice === rowMax;

                return (
                  <div key={r.id} className="bg-surface-lowest border border-border-subtle rounded-xl p-4 space-y-3">
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-on-surface line-clamp-2 text-body-sm flex-1 min-w-0">
                            {r.link ? (
                              <a href={r.link} target="_blank" rel="noopener noreferrer" className="hover:underline">{r.title || '—'}</a>
                            ) : (
                              r.title || '—'
                            )}
                          </div>
                          <button
                            onClick={() => openAnalytics(r.id)}
                            className="rounded bg-primary/15 text-primary hover:bg-primary/25 shrink-0"
                            title="Analytics"
                          >
                            <AnalyticsIcon className="text-[16px]" />
                          </button>
                        </div>
                        <div className="text-[10px] text-on-surface-variant mt-0.5">#{r.id} · {r.sku || '—'}</div>
                        <div className="text-[10px] text-on-surface-variant">{r.category || ''} · <span className="text-primary/70">{r.brand || ''}</span></div>
                        {(r.last_selling_date || r.lastSyncedAt) && (
                          <div className="text-[9px] text-on-surface-variant mt-0.5">
                            {r.last_selling_date && <>Last sold: {formatDate(r.last_selling_date)} @ {formatINR(r.last_selling_price)}</>}
                            {r.last_selling_date && r.lastSyncedAt && <> · </>}
                            {r.lastSyncedAt && <>Last synced: {formatDateTime(r.lastSyncedAt)}</>}
                          </div>
                        )}
                      </div>
                      <div className={`flex flex-col items-end gap-1 shrink-0 ${mdStock === 0 ? 'bg-secondary/20 rounded px-1' : pricedComps.length === 0 ? '' : mdIsMin ? 'bg-success/15 rounded px-1' : mdIsMax ? 'bg-danger/15 rounded px-1' : ''}`}>
                        <span className={`font-mono text-[15px] font-bold ${pricedComps.length > 0 && mdIsMin && mdIsMax ? 'text-success' : pricedComps.length > 0 && mdIsMax ? 'text-danger' : pricedComps.length > 0 && mdIsMin ? 'text-success' : 'text-on-surface'}`}>{formatINR(currentPrice)}</span>
                        {offerPrice !== null && offerPrice !== originalPrice && <span className="font-mono text-[11px] text-on-surface-variant line-through">{formatINR(originalPrice)}</span>}
                      </div>
                    </div>

                    {/* Competitor prices — 2-column grid */}
                    <div className="grid grid-cols-2 gap-2">
                      {competitorTitles.map((title) => {
                        const { price, stock, delta } = compData[title];
                        const hasData = price !== null && String(price) !== 'no product' && Number(price) > 0;
                        const isMin = hasData && price === rowMin;
                        const isMax = hasData && price === rowMax;
                        return (
                          <div key={title} className={`flex items-center justify-between px-2 py-1.5 rounded-lg border ${hasData && stock === 'Out Of Stock' ? 'bg-secondary/20 border-secondary/30' : isMin ? 'bg-success/15 border-success/30' : isMax ? 'bg-danger/15 border-danger/30' : hasData ? 'bg-surface border-border-subtle/50' : ''}`}>
                            <div className="flex flex-col">
                              <span className="text-[10px] text-on-surface-variant font-medium">{prettyLabel(title)}</span>
                              <span className={`font-mono text-[12px] font-semibold ${isMax && stock !== 'Out Of Stock' ? 'text-danger' : isMin && stock !== 'Out Of Stock' ? 'text-success' : price !== null && String(price) !== 'no product' ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                                {price !== null && String(price) !== 'no product' ? formatINR(price) : '—'}
                              </span>
                            </div>
                            {price !== null && String(price) !== 'no product' ? (
                              <div className="flex flex-col items-end gap-0.5">
                                {delta !== null && (
                                  <span className={`text-[9px] font-mono font-semibold ${stock === 'Out Of Stock' ? 'text-on-surface-variant' : delta > 0 ? 'text-success' : 'text-danger'}`}>
                                    {delta > 0 ? '-' : delta < 0 ? '+' : ''}{Math.abs(delta).toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                  </div>
                );
              })}

              {/* Load more */}
              {hasMore && (
                <div className="flex justify-center py-3">
                  {loadingMore ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                  ) : (
                    <button
                      onClick={() => loadPage(skipRef.current, true)}
                      className="px-4 py-2 bg-surface border border-border-subtle rounded text-body-sm text-on-surface hover:bg-surface-high transition-colors"
                    >
                      Load more
                    </button>
                  )}
                </div>
              )}
              {!hasMore && rows.length > 0 && (
                <div className="text-center text-body-sm text-on-surface-variant py-2">
                  End of results — {rows.length} products
                </div>
              )}
            </div>

            {/* Desktop: table — hidden on small/mobile screens */}
            <div className="hidden lg:block px-6 my-6">
              <div className="bg-surface-lowest rounded-xl border border-border-subtle">
                <table className="w-full text-body-sm table-fixed min-w-[900px]">
                  <thead className="border-b border-border-subtle">
                    <tr className="text-left">
                      {COLS.map((col) => {
                        const isProduct = col.key === 'product';
                        return (
                          <th
                            key={col.key}
                            className={`px-3 py-2.5 text-label-caps text-on-surface-variant font-semibold uppercase tracking-wide text-[10px] ${col.width} bg-surface sticky top-16 ${isProduct ? 'left-0 z-20' : 'z-10'}`}
                          >
                            {isProduct ? (
                              <div className="flex items-center justify-between">
                                <span>{col.label}</span>
                                <Tooltip title={
                                  <div className="space-y-1.5 p-1 text-on-surface">
                                    <div className="text-[11px] font-semibold mb-1">Color Guide</div>
                                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-success/15 border border-success/30 inline-block" /><span className="text-[11px]">Lowest price</span></div>
                                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-danger/15 border border-danger/30 inline-block" /><span className="text-[11px]">Highest price</span></div>
                                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-secondary/30 border border-secondary/40 inline-block" /><span className="text-[11px]">Out of stock</span></div>
                                  </div>
                                } placement="bottom" slotProps={{ tooltip: { sx: { backgroundColor: '#ffffff', color: '#1a1a1a', border: '1px solid #e5e5e5' } } }}>
                                  <span className="cursor-pointer text-on-surface-variant hover:text-on-surface"><HelpOutlineIcon className="text-[14px]" /></span>
                                </Tooltip>
                              </div>
                            ) : col.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const originalPrice = Number(r.price) || 0;
                      const offerPrice = r.offer_price != null ? Number(r.offer_price) : null;
                      const currentPrice = offerPrice ?? originalPrice;
                      const mdStock = Number(r.stock) || 0;
                      const rowCompetitors = (r.competitors as Record<string, { price: string; stock: string; link: string }>) || {};

                      const compData = competitorTitles.reduce<Record<string, { price: number | null; stock: string | null; delta: number | null; isLowest: boolean }>>((acc, title) => {
                        const comp = rowCompetitors[title];
                        const rawPrice = comp?.price;
                        const cp = rawPrice !== 'no product' && rawPrice != null ? parseFloat(String(rawPrice)) : null;
                        const cStock = comp?.stock ?? null;
                        const delta = cp !== null && currentPrice > 0 ? ((currentPrice - cp) / cp) * 100 : null;
                        acc[title] = { price: cp, stock: cStock, delta, isLowest: false };
                        return acc;
                      }, {} as Record<string, { price: number | null; stock: string | null; delta: number | null; isLowest: boolean }>);

                      const pricedComps = Object.values(compData).filter((c) => c.price !== null && Number(c.price) > 0 && c.stock !== 'Out Of Stock');
                      const lowestComp = pricedComps.length > 0 ? Math.min(...pricedComps.map((c) => c.price!)) : null;

                      if (lowestComp !== null) {
                        for (const title of competitorTitles) {
                          if (compData[title].price === lowestComp) { compData[title].isLowest = true; break; }
                        }
                      }

                      // Heat map: cheapest/priciest across MD + competitors (excl. OOS and no product)
                      const allPrices = pricedComps.map((c) => c.price!);
                      if (currentPrice > 0) allPrices.push(currentPrice);
                      const rowMin = allPrices.length > 0 ? Math.min(...allPrices) : null;
                      const rowMax = allPrices.length > 0 ? Math.max(...allPrices) : null;
                      const mdIsMin = rowMin !== null && currentPrice === rowMin;
                      const mdIsMax = rowMax !== null && currentPrice === rowMax;

                      return (
                        <tr key={r.id} className="border-b border-border-subtle/50">
                          {/* Product column */}
                          <td className="px-3 py-3 align-top sticky left-0 z-10 bg-surface-lowest">
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-on-surface line-clamp-2 text-body-sm flex-1 min-w-0">
                                {r.link ? (
                                  <a href={r.link} target="_blank" rel="noopener noreferrer" className="hover:underline">{r.title || '—'}</a>
                                ) : (
                                  r.title || '—'
                                )}
                              </div>
                              <button
                                onClick={() => openAnalytics(r.id)}
                                className="rounded bg-primary/15 text-primary hover:bg-primary/25 shrink-0"
                                title="Analytics"
                              >
                                <AnalyticsIcon className="text-[14px]" />
                              </button>
                            </div>
                            <div className="text-[10px] text-on-surface-variant mt-0.5">#{r.id} · {r.sku || '—'} · {r.category || ''} · <span className="text-primary/70">{r.brand || ''}</span></div>
                            {(r.last_selling_date || r.lastSyncedAt) && (
                              <div className="text-[9px] text-on-surface-variant mt-0.5">
                                {r.last_selling_date && <>Last sold: {formatDate(r.last_selling_date)} @ {formatINR(r.last_selling_price)}</>}
                                {r.last_selling_date && r.lastSyncedAt && <> · </>}
                                {r.lastSyncedAt && <>Last synced: {formatDateTime(r.lastSyncedAt)}</>}
                              </div>
                            )}
                          </td>

                          {/* MD column */}
                          <td className={`px-3 py-3 align-top ${mdStock === 0 ? 'bg-secondary/20' : pricedComps.length === 0 ? '' : mdIsMin ? 'bg-success/15' : mdIsMax ? 'bg-danger/15' : ''}`}>
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-mono text-[13px] font-semibold ${pricedComps.length > 0 && mdIsMin && mdIsMax ? 'text-success' : pricedComps.length > 0 && mdIsMax ? 'text-danger' : pricedComps.length > 0 && mdIsMin ? 'text-success' : 'text-on-surface'}`}>{formatINR(currentPrice)}</span>
                              {offerPrice !== null && offerPrice !== originalPrice && <span className="font-mono text-[11px] text-on-surface-variant line-through">{formatINR(originalPrice)}</span>}
                            </div>
                          </td>

                          {/* Competitor columns */}
                          {competitorTitles.map((title) => {
                            const { price, stock, delta } = compData[title];
                            const hasData = price !== null && String(price) !== 'no product' && Number(price) > 0;
                            const isMin = hasData && price === rowMin;
                            const isMax = hasData && price === rowMax;
                            return (
                              <td key={title} className={`px-2 py-3 align-top ${hasData && stock === 'Out Of Stock' ? 'bg-secondary/20' : isMin ? 'bg-success/15' : isMax ? 'bg-danger/15' : ''}`}>
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`font-mono text-[12px] font-semibold ${isMax && stock !== 'Out Of Stock' ? 'text-danger' : isMin && stock !== 'Out Of Stock' ? 'text-success' : hasData ? 'text-on-surface' : 'text-on-surface-variant'}`}>{hasData ? formatINR(price) : '—'}</span>
                                  {hasData && delta !== null && <span className={`text-[9px] font-mono font-semibold ${stock === 'Out Of Stock' ? 'text-on-surface-variant' : delta > 0 ? 'text-success' : 'text-danger'}`}>{delta > 0 ? '-' : delta < 0 ? '+' : ''}{Math.abs(delta).toFixed(1)}%</span>}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Desktop footer */}
                {hasMore && (
                  <div className="flex justify-center py-4">
                    {loadingMore ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                    ) : (
                      <button onClick={() => loadPage(skipRef.current, true)} className="px-4 py-2 bg-surface border border-border-subtle rounded text-body-sm text-on-surface hover:bg-surface-high transition-colors">Load more</button>
                    )}
                  </div>
                )}
                {!hasMore && rows.length > 0 && (
                  <div className="text-center text-body-sm text-on-surface-variant py-3 border-t border-border-subtle/50">End of results — {rows.length} products</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Analytics Modal */}
      {analyticsRow !== null && (() => {
        const r = rows.find((row) => row.id === analyticsRow);
        if (!r) return null;
        const originalPrice = Number(r.price) || 0;
        const offerPrice = r.offer_price != null ? Number(r.offer_price) : null;
        const currentPrice = offerPrice ?? originalPrice;
        const productLinks = scrapperLinks[analyticsRow] || [];
        const rowCompetitors = (r.competitors as Record<string, { price: string; stock: string; link: string }>) || {};

        // A competitor link is only clickable when that competitor actually has a
        // price for the selected day - "link exists but no price" was the
        // reported anomaly. The URL editor still gets the known URL, so a link
        // without a price can always be corrected.
        const findLink = (title: string): string | undefined => {
          const comp = rowCompetitors[title];
          if (!comp) return undefined;
          const hasPrice = comp.price != null && String(comp.price) !== 'no product' && Number(comp.price) > 0;
          if (!hasPrice) return undefined;
          return comp.link || undefined;
        };

        // Best-known URL for a competitor, with or without a price.
        const findEditableLink = (title: string): string | undefined => {
          const direct = rowCompetitors[title]?.link;
          if (direct) return direct;
          const label = prettyLabel(title).toLowerCase();
          const match = productLinks.find((l) => l.hasPrice && l.title.toLowerCase().includes(label))
            || productLinks.find((l) => l.title.toLowerCase().includes(label));
          return match?.url;
        };

        const competitorRows = competitorTitles.map((title) => {
          const comp = rowCompetitors[title];
          const price = comp?.price ?? null;
          const stock = comp?.stock ?? null;
          const hasData = price !== null && String(price) !== 'no product' && Number(price) > 0;
          const hasStock = stock === 'In Stock' || stock === 'Out Of Stock';
          return {
            key: title,
            link: findLink(title),
            editLink: findEditableLink(title),
            price,
            stock,
            hasData,
            hasStock,
          };
        });
        // Market metrics use in-stock only (OOS competitors excluded from min and vs-MD diff)
        const marketRows = competitorRows.filter((c) => c.hasData && c.stock === 'In Stock');
        const marketPrices = marketRows.map((c) => parseFloat(String(c.price))).filter((p) => !isNaN(p));
        const lowestComp = marketPrices.length > 0 ? Math.min(...marketPrices) : null;
        const lowestCompRow = marketRows.find((c) => parseFloat(String(c.price)) === lowestComp);

        // Market metrics from latest sparkline date
        let sparklineLowestComp: number | null = null;
        let sparklineLowestCompKey: string | null = null;
        if (sparklineData.length > 0) {
          const latestEntry = sparklineData[sparklineData.length - 1] as Record<string, unknown>;
          const compPrices: { key: string; price: number }[] = [];
          for (const title of competitorTitles) {
            const v = parseFloat(String(latestEntry[title] ?? 'NaN'));
            if (!isNaN(v) && v > 0) compPrices.push({ key: title, price: v });
          }
          if (compPrices.length > 0) {
            const min = Math.min(...compPrices.map((c) => c.price));
            sparklineLowestComp = min;
            sparklineLowestCompKey = compPrices.find((c) => c.price === min)?.key || null;
          }
        }
        const marketMin = sparklineLowestComp !== null ? sparklineLowestComp : lowestComp;
        const marketMinKey = sparklineLowestCompKey !== null ? sparklineLowestCompKey : lowestCompRow?.key || null;

        // Cost Master (DP+ / NLC+ / NLC All) — raw values only. No margin and no
        // suggested price is computed here; the client decides the price.
        const costTiles = [
          { label: 'DP+', value: r.cost_dp },
          { label: 'NLC+', value: r.cost_nlc },
          { label: 'NLC All', value: r.cost_nlc_all },
        ];
        // "Synced" means the value is present as a number — a genuine 0 is still
        // real data (it must not be hidden behind the "no data" message), while
        // null/undefined means the Cost Master row is absent.
        const hasCost = costTiles.some((t) => typeof t.value === 'number');

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setAnalyticsRow(null)}>
            <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl border border-border-subtle max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle bg-surface-lowest rounded-t-xl sticky top-0 z-10">
                <div>
                  <h3 className="font-headline-sm text-on-surface font-semibold">Price Analytics</h3>
                  <p className="text-body-sm text-on-surface-variant line-clamp-1 mt-0.5">{r.title}</p>
                </div>
                <button onClick={() => setAnalyticsRow(null)} className="p-1 rounded hover:bg-surface-high transition-colors">
                  <CloseIcon className="text-on-surface-variant text-[20px]" />
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* MD Price summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-surface-lowest border border-border-subtle rounded-lg p-3 text-center">
                    <div className="text-label-caps text-on-surface-variant text-[10px] uppercase">MD Price</div>
                    <div className="font-headline-sm font-bold text-on-surface font-mono mt-1">{formatINR(currentPrice)}</div>
                  </div>
                  <div className="bg-surface-lowest border border-border-subtle rounded-lg p-3 text-center">
                    <div className="text-label-caps text-on-surface-variant text-[10px] uppercase">Market Min</div>
                    <div className="font-headline-sm font-bold text-on-surface font-mono mt-1">{formatINR(marketMin)}</div>
                  </div>
                  <div className="bg-surface-lowest border border-border-subtle rounded-lg p-3 text-center">
                    <div className="text-label-caps text-on-surface-variant text-[10px] uppercase">Gap to Match</div>
                    <div className={`font-headline-sm font-bold font-mono mt-1 ${marketMin !== null && currentPrice > marketMin ? 'text-danger' : 'text-success'}`}>
                      {marketMin !== null
                        ? `${currentPrice > marketMin ? '-' : currentPrice < marketMin ? '+' : ''}${formatINR(Math.abs(currentPrice - marketMin))}`
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Cost Master — DP+ / NLC+ / NLC All (raw values, no computed margin) */}
                <div>
                  <div className="text-label-caps text-on-surface-variant text-[10px] uppercase mb-2">Cost Master</div>
                  {hasCost ? (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        {costTiles.map((t) => (
                          <div key={t.label} className="bg-surface-lowest border border-border-subtle rounded-lg p-3 text-center">
                            <div className="text-label-caps text-on-surface-variant text-[10px] uppercase">{t.label}</div>
                            <div className="font-headline-sm font-bold text-on-surface font-mono mt-1">{formatINR(t.value)}</div>
                          </div>
                        ))}
                      </div>
                      {r.cost_updated_at && (
                        <div className="text-[10px] text-on-surface-variant mt-1.5">
                          Cost updated: {formatDateTime(r.cost_updated_at)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-body-sm text-on-surface-variant">
                      No Cost Master data synced for this product.
                    </div>
                  )}
                </div>

                {/* Stock badge */}
                <div className="flex items-center gap-2">
                  {marketMinKey && marketMin !== null && (
                    <span className="text-body-sm text-on-surface-variant">
                      Lowest at {prettyLabel(marketMinKey)}: {formatINR(marketMin)}
                    </span>
                  )}
                </div>

                {/* Price Trend */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-label-caps text-on-surface-variant text-[10px] uppercase">Price History</div>
                    <div className="flex gap-1">
                      {[{ label: '7D', value: 7 }, { label: '15D', value: 15 }, { label: '30D', value: 30 }, { label: 'All', value: 0 }].map(({ label, value }) => (
                        <button
                          key={value}
                          onClick={() => {
                            setSparklineDaysBack(value);
                            if (analyticsRow !== null) {
                              fetchSparkline(analyticsRow, value);
                            }
                          }}
                          className={`px-2 py-0.5 text-[10px] rounded ${sparklineDaysBack === value ? 'bg-primary text-on-primary font-semibold' : 'text-on-surface-variant hover:bg-surface-high'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {sparklineLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" /></div>
                  ) : sparklineData.length > 0 ? (
                    <div className="bg-surface-lowest border border-border-subtle rounded-lg p-3">
                      <div style={{ height: 180 }}>
                        <Line
                          data={{
                            labels: sparklineData.map((d) => String(d.date || '').slice(5, 10)),
                            datasets: [
                              {
                                label: 'MD',
                                data: sparklineData.map((d) => {
                                  const v = Number((d as Record<string, unknown>).md);
                                  return isNaN(v) ? null : v;
                                }),
                                borderColor: '#22C55E',
                                backgroundColor: 'rgba(34,197,94,0.1)',
                                fill: true,
                                tension: 0.3,
                                pointRadius: 2,
                              },
                              ...competitorTitles
                                .filter((title) => {
                                  const vals = sparklineData.map((d) => parseFloat(String((d as Record<string, unknown>)[title] ?? 'NaN')));
                                  return vals.some((v) => !isNaN(v));
                                })
                                .map((title) => ({
                                  label: prettyLabel(title),
                                  data: sparklineData.map((d) => {
                                    const v = parseFloat(String((d as Record<string, unknown>)[title] ?? 'NaN'));
                                    return isNaN(v) ? null : v;
                                  }),
                                  borderColor: hashColor(title),
                                  backgroundColor: 'transparent',
                                  tension: 0.3,
                                  pointRadius: 2,
                                })),
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { position: 'bottom' as const, labels: { boxWidth: 10, font: { size: 9 }, color: '#434654' } },
                              tooltip: { mode: 'index' as const, intersect: false },
                            },
                            scales: {
                              y: {
                                ticks: { callback: (v) => `₹${Number(v).toLocaleString()}`, font: { size: 9 }, color: '#434654' },
                                grid: { color: '#E2E8F0' },
                              },
                              x: {
                                ticks: { maxTicksLimit: 8, font: { size: 9 }, color: '#434654' },
                                grid: { display: false },
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  ) : sparklineError ? (
                    <div className="bg-surface-lowest border border-border-subtle rounded-lg p-4 text-center text-body-sm text-danger flex items-center justify-center gap-2">
                      <ErrorIcon className="text-[14px]" />Failed to load price history
                    </div>
                  ) : (
                    <div className="bg-surface-lowest border border-border-subtle rounded-lg p-4 text-center text-body-sm text-on-surface-variant">
                      No price history available
                    </div>
                  )}
                </div>

                {/* Competitor prices table */}
                <div>
                  <div className="text-label-caps text-on-surface-variant text-[10px] uppercase mb-2">Competitor Prices</div>
                  <div className="bg-surface-lowest border border-border-subtle rounded-lg overflow-hidden">
                    <table className="w-full text-body-sm">
                      <thead className="bg-surface border-b border-border-subtle">
                        <tr>
                          <th className="px-3 py-2 text-left text-label-caps text-on-surface-variant text-[10px] uppercase">Company</th>
                          <th className="px-3 py-2 text-right text-label-caps text-on-surface-variant text-[10px] uppercase">Price</th>
                          <th className="px-3 py-2 text-center text-label-caps text-on-surface-variant text-[10px] uppercase">Stock</th>
                          <th className="px-3 py-2 text-right text-label-caps text-on-surface-variant text-[10px] uppercase">vs MD</th>
                          <th className="px-3 py-2 text-right text-label-caps text-on-surface-variant text-[10px] uppercase">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {competitorRows.map((c) => {
                          const cp = c.hasData ? parseFloat(String(c.price)) : NaN;
                          const isCompLowest = c.hasData && c.stock === 'In Stock' && marketMin !== null && cp === marketMin;
                          const showDiff = c.hasData && c.stock === 'In Stock' && currentPrice > 0;
                          const diff = showDiff ? cp - currentPrice! : 0;
                          const stockBadge = c.stock === 'In Stock'
                            ? <span className="font-semibold text-success">IN</span>
                            : c.stock === 'Out Of Stock'
                              ? <span className="font-semibold text-danger">OUT</span>
                              : <span className="text-on-surface-variant">—</span>;
                          const isEditing = editingKey === c.key;
                          const trimmed = editUrl.trim();
                          const canSave = trimmed !== '' && /^https?:\/\//.test(trimmed) && trimmed !== (c.editLink || '');
                          return (
                            <Fragment key={c.key}>
                              <tr className="border-b border-border-subtle/50 last:border-0">
                                <td className="px-3 py-2 font-semibold text-on-surface text-[13px]">
                                  {c.link ? (
                                    <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                      {prettyLabel(c.key)}
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </a>
                                  ) : (
                                    <span className="text-on-surface">{prettyLabel(c.key)}</span>
                                  )}
                                </td>
                                <td className={`px-3 py-2 text-right font-mono text-[13px] ${isCompLowest ? 'text-success font-semibold' : c.hasData ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                                  {c.hasData ? formatINR(cp) : '—'}
                                </td>
                                <td className="px-3 py-2 text-center text-[12px]">{stockBadge}</td>
                                <td className="px-3 py-2 text-right">
                                  {showDiff && (
                                    <span className={`font-mono text-[11px] font-semibold ${diff < 0 ? 'text-success' : 'text-danger'}`}>
                                      {diff < 0 ? '-' : diff > 0 ? '+' : ''}{Math.abs(diff).toFixed(0)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (isEditing) { setEditingKey(null); setEditUrl(''); }
                                      else { setEditingKey(c.key); setEditUrl(c.editLink || ''); }
                                    }}
                                    className="p-1 rounded hover:bg-primary/10 text-on-surface-variant"
                                    title={c.editLink ? 'Edit URL' : 'Add URL'}
                                  >
                                    {c.editLink ? <EditIcon className="text-[14px]" /> : <AddIcon className="text-[14px]" />}
                                  </button>
                                </td>
                              </tr>
                              {isEditing && (
                                <tr className="bg-surface">
                                  <td colSpan={5} className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        autoFocus
                                        type="text"
                                        value={editUrl}
                                        onChange={(e) => setEditUrl(e.target.value)}
                                        disabled={savingEdit}
                                        placeholder="https://..."
                                        className="flex-1 min-w-0 px-2 py-1 text-[12px] font-mono bg-surface-lowest border border-border-subtle rounded focus:outline-none focus:ring-2 focus:ring-primary/20"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleSaveLink(c.key)}
                                        disabled={savingEdit || !canSave}
                                        className="px-3 py-1 text-[11px] font-semibold rounded bg-primary text-on-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => { setEditingKey(null); setEditUrl(''); }}
                                        className="px-3 py-1 text-[11px] font-semibold rounded bg-surface border border-border-subtle text-on-surface-variant"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="px-5 py-4 border-t border-border-subtle flex gap-3 bg-surface-lowest rounded-b-xl">
                <button
                  onClick={() => handleDownload(r.id)}
                  disabled={downloading !== null}
                  className="flex-1 bg-surface border border-border-subtle py-2 rounded-lg text-body-sm font-medium text-on-surface hover:bg-surface-high transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading === r.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-on-surface/30 border-t-on-surface" />
                      Exporting…
                    </>
                  ) : (
                    <>
                      <DownloadIcon className="text-[16px]" /> Export
                    </>
                  )}
                </button>
                {r.link ? (
                  <a
                    href={r.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-primary text-on-primary py-2 rounded-lg text-body-sm font-semibold hover:bg-primary-container transition-colors text-center"
                  >
                    View on MD Computers
                  </a>
                ) : (
                  <button disabled className="flex-1 bg-primary text-on-primary py-2 rounded-lg text-body-sm font-semibold opacity-50 cursor-not-allowed">
                    View on MD Computers
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Scrapper Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)}>
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md border border-border-subtle animate-toast" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <h3 className="font-headline-sm text-on-surface">Add Competitor</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 rounded hover:bg-surface-high transition-colors">
                <CloseIcon className="text-on-surface-variant text-[20px]" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] text-label-caps text-on-surface-variant uppercase block mb-1">Product ID</label>
                <div className="bg-surface-high px-3 py-2 rounded-lg text-body-sm text-on-surface border border-border-subtle">{addProdId}</div>
              </div>
              <div>
                <label className="text-[10px] text-label-caps text-on-surface-variant uppercase block mb-1">Company</label>
                {addLoading ? (
                  <div className="flex justify-center py-2"><div className="animate-spin rounded-full h-4 w-4 border border-primary border-t-transparent" /></div>
                ) : (
                  <select
                    value={addTitle}
                    onChange={(e) => setAddTitle(e.target.value)}
                    className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  >
                    <option value="">Select company...</option>
                    {addTitles.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className="text-[10px] text-label-caps text-on-surface-variant uppercase block mb-1">URL</label>
                <textarea
                  value={addUrl}
                  onChange={(e) => setAddUrl(e.target.value)}
                  placeholder="https://..."
                  rows={3}
                  className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-label-caps text-on-surface-variant uppercase block mb-1">Status</label>
                <select
                  value={addStatus}
                  onChange={(e) => setAddStatus(e.target.value)}
                  className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                >
                  <option value="w">Waiting</option>
                  <option value="a">Active</option>
                  <option value="i">Inactive</option>
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-border-subtle flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-surface border border-border-subtle py-2 rounded-lg text-body-sm font-medium text-on-surface hover:bg-surface-high transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNew}
                disabled={!addTitle || !addUrl}
                className="flex-1 bg-primary-container text-on-primary py-2 rounded-lg text-body-sm font-semibold hover:bg-primary transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <AddIcon className="text-[16px]" /> Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comparison;
